import * as THREE from 'three'
import {
  type GlbDatumOriginFrame,
  type GlbDatumPlaneFrame,
  type GlbDatumSlotFrame,
  type GlbDatumSlotTargetDescriptor,
  type GlbDatumSlotKind,
} from './types'
export type {
  GlbDatumOriginFrame,
  GlbDatumPlaneFrame,
  GlbDatumSlotFrame,
  GlbDatumSlotKind,
  GlbDatumSlotTargetDescriptor,
} from './types'
export { isSameGlbDatumSlotDescriptor } from './types'

/**
 * 平面在 AABB 基础上的外扩倍数。
 * 沿用 SolidWorks 默认基准面"略大于模型"的观感，避免矩形贴死模型边缘视觉发糊。
 */
const PLANE_AABB_MARGIN = 1.2

/** 原点显示尺寸下限（像素提示）。 */
const MIN_ORIGIN_SIZE_PX = 16

/** 原点显示尺寸上限（像素提示）。 */
const MAX_ORIGIN_SIZE_PX = 24

/** 原点显示尺寸缩放因子。 */
const ORIGIN_SCREEN_SIZE_SCALE = 2

/**
 * 解析 GLB 基准槽位 frame。
 *
 * 说明：
 * 1. 包围盒使用 `Box3.setFromObject(hostObject)` —— 世界 AABB，与 imported-model part/assembly hover 的 bbox 高亮同源；
 * 2. plane 的 width/height 取 AABB 沿 `basis.widthAxis / heightAxis` 对应世界轴的分量，再乘 {@link PLANE_AABB_MARGIN} 给点视觉余量；
 * 3. plane 矩形以 `origin`（= 宿主世界原点）为中心绘制，与 SolidWorks 默认平面"过模型原点"语义一致。
 *
 * @param scene 当前场景。
 * @param descriptor 槽位描述符。
 * @returns 解析成功时返回世界 frame；宿主不存在或无几何时返回 null。
 */
export function resolveGlbDatumSlotFrame(
  scene: THREE.Scene,
  descriptor: GlbDatumSlotTargetDescriptor,
): GlbDatumSlotFrame | null {
  scene.updateMatrixWorld(true)
  const hostObject = findSceneObjectById(scene, descriptor.hostObjectId)
  if (!hostObject) {
    return null
  }

  hostObject.updateMatrixWorld(true)
  const worldBox = new THREE.Box3().setFromObject(hostObject)
  if (worldBox.isEmpty()) {
    return null
  }

  const origin = new THREE.Vector3().setFromMatrixPosition(hostObject.matrixWorld)

  if (descriptor.slotKind === 'origin') {
    return buildOriginFrame(worldBox, origin)
  }

  return buildPlaneFrame(worldBox, origin, hostObject.matrixWorld, descriptor.slotKind)
}

/**
 * 根据宿主对象 ID 在场景中查找对象。
 *
 * @param scene 当前场景。
 * @param objectId 目标对象 ID。
 * @returns 命中的场景对象；未命中时返回 null。
 */
function findSceneObjectById(scene: THREE.Scene, objectId: string): THREE.Object3D | null {
  let targetObject: THREE.Object3D | null = null
  scene.traverse((object) => {
    if (targetObject) {
      return
    }
    if (object.uuid === objectId) {
      targetObject = object
    }
  })
  return targetObject
}

/**
 * 构建原点 frame。
 *
 * @param worldBox 宿主世界 AABB。
 * @param origin 宿主世界原点。
 * @returns 原点 frame。
 */
function buildOriginFrame(worldBox: THREE.Box3, origin: THREE.Vector3): GlbDatumOriginFrame {
  const size = new THREE.Vector3()
  worldBox.getSize(size)
  const maxSpan = Math.max(size.x, size.y, size.z)
  return {
    kind: 'origin',
    slotKind: 'origin',
    origin: origin.clone(),
    sizePx: clampOriginSizePx(maxSpan),
  }
}

/**
 * 构建平面 frame。
 *
 * @param worldBox 宿主世界 AABB。
 * @param origin 宿主世界原点。
 * @param worldMatrix 宿主世界矩阵。
 * @param kind 平面类型。
 * @returns 平面 frame。
 */
function buildPlaneFrame(
  worldBox: THREE.Box3,
  origin: THREE.Vector3,
  worldMatrix: THREE.Matrix4,
  kind: Exclude<GlbDatumSlotKind, 'origin'>,
): GlbDatumPlaneFrame {
  const basis = resolvePlaneBasis(kind)
  const rotation = new THREE.Matrix4().extractRotation(worldMatrix)
  const xDir = basis.xDir.clone().applyMatrix4(rotation).normalize()
  const yDir = basis.yDir.clone().applyMatrix4(rotation).normalize()
  const normal = basis.normal.clone().applyMatrix4(rotation).normalize()

  const size = new THREE.Vector3()
  worldBox.getSize(size)
  const width = pickAabbExtentAlongCardinalAxis(size, basis.xDir) * PLANE_AABB_MARGIN
  const height = pickAabbExtentAlongCardinalAxis(size, basis.yDir) * PLANE_AABB_MARGIN

  return {
    kind: 'plane',
    slotKind: kind,
    origin: origin.clone(),
    xDir,
    yDir,
    normal,
    width,
    height,
  }
}

/**
 * 解析 SolidWorks 默认平面的本地基向量。
 *
 * @param kind 平面类型。
 * @returns 平面本地基向量。
 */
function resolvePlaneBasis(kind: Exclude<GlbDatumSlotKind, 'origin'>): {
  xDir: THREE.Vector3
  yDir: THREE.Vector3
  normal: THREE.Vector3
} {
  if (kind === 'front-plane') {
    return {
      xDir: new THREE.Vector3(1, 0, 0),
      yDir: new THREE.Vector3(0, 1, 0),
      normal: new THREE.Vector3(0, 0, 1),
    }
  }
  if (kind === 'top-plane') {
    return {
      xDir: new THREE.Vector3(0, 0, 1),
      yDir: new THREE.Vector3(1, 0, 0),
      normal: new THREE.Vector3(0, 1, 0),
    }
  }
  return {
    xDir: new THREE.Vector3(0, 1, 0),
    yDir: new THREE.Vector3(0, 0, 1),
    normal: new THREE.Vector3(1, 0, 0),
  }
}

/**
 * 按 `(1,0,0) / (0,1,0) / (0,0,1)` 这种基数方向把 AABB 三维尺寸映射到单个标量。
 *
 * @param size `Box3.getSize()` 结果。
 * @param cardinalAxis `(1,0,0) / (0,1,0) / (0,0,1)` 之一。
 * @returns 对应世界轴长度。
 */
function pickAabbExtentAlongCardinalAxis(size: THREE.Vector3, cardinalAxis: THREE.Vector3): number {
  if (Math.abs(cardinalAxis.x) >= 0.5) {
    return size.x
  }
  if (Math.abs(cardinalAxis.y) >= 0.5) {
    return size.y
  }
  return size.z
}

/**
 * 钳制原点显示尺寸。
 *
 * @param size 原始跨度。
 * @returns 原点显示尺寸提示。
 */
function clampOriginSizePx(size: number): number {
  const scaledSize = Number.isFinite(size) && size > 0 ? Math.round(size * ORIGIN_SCREEN_SIZE_SCALE) : 0
  return Math.min(
    Math.max(scaledSize, MIN_ORIGIN_SIZE_PX),
    MAX_ORIGIN_SIZE_PX,
  )
}
